import React, { useState, useEffect } from 'react';
import { leadAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './LeadDetails.css';

const LeadDetails = ({ lead, onClose, onUpdate }) => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [leadData, setLeadData] = useState(lead);
  const [loading, setLoading] = useState(false);
  
  // Follow-up form state
  const [followUpForm, setFollowUpForm] = useState({
    scheduledDate: '',
    scheduledTime: '',
    purpose: '',
    notes: '',
  });

  // Communication form state
  const [communicationForm, setCommunicationForm] = useState({
    type: 'Call',
    subject: '',
    message: '',
  });

  // Note form state
  const [noteForm, setNoteForm] = useState({
    content: '',
    isImportant: false,
  });

  useEffect(() => {
    if (lead) {
      fetchLeadDetails();
    }
  }, [lead._id]);

  const fetchLeadDetails = async () => {
    try {
      const response = await leadAPI.getById(lead._id);
      setLeadData(response.data);
    } catch (error) {
      console.error('Error fetching lead details:', error);
    }
  };

  const handleAddFollowUp = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Combine date and time
      const scheduledDateTime = new Date(`${followUpForm.scheduledDate}T${followUpForm.scheduledTime}`);
      
      await leadAPI.addFollowUp(leadData._id, {
        scheduledDate: scheduledDateTime,
        purpose: followUpForm.purpose,
        notes: followUpForm.notes,
        status: 'Pending',
      });

      alert('Follow-up scheduled successfully!');
      setFollowUpForm({
        scheduledDate: '',
        scheduledTime: '',
        purpose: '',
        notes: '',
      });
      fetchLeadDetails();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error adding follow-up:', error);
      alert('Failed to add follow-up');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCommunication = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await leadAPI.addCommunication(leadData._id, communicationForm);
      alert('Communication logged successfully!');
      setCommunicationForm({
        type: 'Call',
        subject: '',
        message: '',
      });
      fetchLeadDetails();
    } catch (error) {
      console.error('Error adding communication:', error);
      alert('Failed to log communication');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await leadAPI.addNote(leadData._id, noteForm);
      alert('Note added successfully!');
      setNoteForm({
        content: '',
        isImportant: false,
      });
      fetchLeadDetails();
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFollowUpStatus = async (followupId, status) => {
    try {
      setLoading(true);
      await leadAPI.updateFollowUpStatus(leadData._id, followupId, status);
      alert(`Follow-up marked as ${status}!`);
      fetchLeadDetails();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating follow-up status:', error);
      alert('Failed to update follow-up status');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateOnly = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="lead-details-overlay" onClick={onClose}>
      <div className="lead-details-container" onClick={(e) => e.stopPropagation()}>
        <div className="lead-details-header">
          <div className="header-left">
            <h2>{leadData.customerName}</h2>
            <span className="lead-id">{leadData.leadId}</span>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📋 Overview
          </button>
          <button 
            className={`tab ${activeTab === 'followups' ? 'active' : ''}`}
            onClick={() => setActiveTab('followups')}
          >
            📅 Follow-ups
          </button>
          <button 
            className={`tab ${activeTab === 'communication' ? 'active' : ''}`}
            onClick={() => setActiveTab('communication')}
          >
            💬 Communication
          </button>
          <button 
            className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            📝 Notes
          </button>
        </div>

        <div className="lead-details-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="details-grid">
                <div className="detail-item">
                  <label>Customer Name</label>
                  <p>{leadData.customerName}</p>
                </div>
                <div className="detail-item">
                  <label>Mobile</label>
                  <p>{leadData.mobile}</p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p>{leadData.email || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>Lead Source</label>
                  <p>{leadData.leadSource}</p>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <p><span className={`badge badge-${leadData.leadStatus.toLowerCase().replace(/\s+/g, '-')}`}>{leadData.leadStatus}</span></p>
                </div>
                <div className="detail-item">
                  <label>Sales Stage</label>
                  <p><span className={`badge badge-stage`}>{leadData.salesStage}</span></p>
                </div>
                <div className="detail-item">
                  <label>Priority</label>
                  <p><span className={`badge badge-priority-${leadData.priority.toLowerCase()}`}>{leadData.priority}</span></p>
                </div>
                <div className="detail-item">
                  <label>Deal Value</label>
                  <p>₹{leadData.dealValue?.toLocaleString() || 0}</p>
                </div>
                <div className="detail-item">
                  <label>Probability</label>
                  <p>{leadData.probability}%</p>
                </div>
                <div className="detail-item">
                  <label>Timeline</label>
                  <p>{leadData.timeline || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>Assigned To</label>
                  <p>{leadData.assignedTo?.name || 'Unassigned'}</p>
                </div>
                <div className="detail-item">
                  <label>Created Date</label>
                  <p>{formatDateOnly(leadData.createdAt)}</p>
                </div>
              </div>
              
              {leadData.productInterest && (
                <div className="detail-section">
                  <label>Product Interest</label>
                  <p>{leadData.productInterest}</p>
                </div>
              )}
              
              {leadData.requirement && (
                <div className="detail-section">
                  <label>Requirement</label>
                  <p>{leadData.requirement}</p>
                </div>
              )}
              
              {leadData.address && (
                <div className="detail-section">
                  <label>Address</label>
                  <p>
                    {[
                      leadData.address.street,
                      leadData.address.city,
                      leadData.address.state,
                      leadData.address.pincode
                    ].filter(Boolean).join(', ') || 'N/A'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Follow-ups Tab */}
          {activeTab === 'followups' && (
            <div className="followups-tab">
              <div className="add-followup-section">
                <h3>📅 Schedule Follow-up</h3>
                <form onSubmit={handleAddFollowUp} className="followup-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Date *</label>
                      <input
                        type="date"
                        value={followUpForm.scheduledDate}
                        onChange={(e) => setFollowUpForm({ ...followUpForm, scheduledDate: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Time *</label>
                      <input
                        type="time"
                        value={followUpForm.scheduledTime}
                        onChange={(e) => setFollowUpForm({ ...followUpForm, scheduledTime: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Purpose *</label>
                    <input
                      type="text"
                      value={followUpForm.purpose}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, purpose: e.target.value })}
                      placeholder="e.g., Product demo, Price discussion"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={followUpForm.notes}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, notes: e.target.value })}
                      placeholder="Additional notes..."
                      rows="3"
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Scheduling...' : '⏰ Schedule Follow-up'}
                  </button>
                </form>
              </div>

              <div className="followups-list">
                <h3>Follow-up History</h3>
                {leadData.followUps && leadData.followUps.length > 0 ? (
                  <div className="timeline">
                    {leadData.followUps
                      .sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate))
                      .map((followUp, index) => (
                        <div key={index} className={`timeline-item status-${followUp.status.toLowerCase()}`}>
                          <div className="timeline-marker"></div>
                          <div className="timeline-content">
                            <div className="timeline-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className="timeline-date">{formatDate(followUp.scheduledDate)}</span>
                                <span className={`status-badge ${followUp.status.toLowerCase()}`}>
                                  {followUp.status}
                                </span>
                              </div>
                              {followUp.status === 'Pending' && (
                                <div className="followup-actions" style={{ display: 'flex', gap: '6px' }}>
                                  <button
                                    onClick={() => handleUpdateFollowUpStatus(followUp._id, 'Completed')}
                                    className="btn btn-secondary"
                                    style={{ padding: '3px 8px', fontSize: '11px', background: '#27ae60' }}
                                    title="Mark Completed"
                                  >
                                    Done
                                  </button>
                                  <button
                                    onClick={() => handleUpdateFollowUpStatus(followUp._id, 'Missed')}
                                    className="btn btn-secondary"
                                    style={{ padding: '3px 8px', fontSize: '11px', background: '#c0392b' }}
                                    title="Mark Missed"
                                  >
                                    Missed
                                  </button>
                                </div>
                              )}
                            </div>
                            <h4>{followUp.purpose}</h4>
                            {followUp.notes && <p>{followUp.notes}</p>}
                            {followUp.assignedTo && (
                              <small className="assigned-info">
                                Assigned to: {followUp.assignedTo.name}
                              </small>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="no-data">No follow-ups scheduled yet</p>
                )}
              </div>
            </div>
          )}

          {/* Communication Tab */}
          {activeTab === 'communication' && (
            <div className="communication-tab">
              <div className="add-communication-section">
                <h3>💬 Log Communication</h3>
                <form onSubmit={handleAddCommunication} className="communication-form">
                  <div className="form-group">
                    <label>Type *</label>
                    <select
                      value={communicationForm.type}
                      onChange={(e) => setCommunicationForm({ ...communicationForm, type: e.target.value })}
                      required
                    >
                      <option value="Call">Call</option>
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="Email">Email</option>
                      <option value="SMS">SMS</option>
                      <option value="Meeting">Meeting</option>
                      <option value="Note">Note</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Subject *</label>
                    <input
                      type="text"
                      value={communicationForm.subject}
                      onChange={(e) => setCommunicationForm({ ...communicationForm, subject: e.target.value })}
                      placeholder="Brief subject..."
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Message *</label>
                    <textarea
                      value={communicationForm.message}
                      onChange={(e) => setCommunicationForm({ ...communicationForm, message: e.target.value })}
                      placeholder="Conversation details..."
                      rows="4"
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : '💾 Log Communication'}
                  </button>
                </form>
              </div>

              <div className="communication-list">
                <h3>Communication History</h3>
                {leadData.communicationHistory && leadData.communicationHistory.length > 0 ? (
                  <div className="timeline">
                    {leadData.communicationHistory
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((comm, index) => (
                        <div key={index} className="timeline-item">
                          <div className="timeline-marker comm-marker"></div>
                          <div className="timeline-content">
                            <div className="timeline-header">
                              <span className="comm-type">{comm.type}</span>
                              <span className="timeline-date">{formatDate(comm.date)}</span>
                            </div>
                            <h4>{comm.subject}</h4>
                            <p>{comm.message}</p>
                            {comm.recordedBy && (
                              <small className="recorded-by">
                                By: {comm.recordedBy.name}
                              </small>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="no-data">No communication logged yet</p>
                )}
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="notes-tab">
              <div className="add-note-section">
                <h3>📝 Add Note</h3>
                <form onSubmit={handleAddNote} className="note-form">
                  <div className="form-group">
                    <label>Select Standard Note</label>
                    <select 
                      onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                      value={[
                        'Follow-up',
                        'Not Pick Up',
                        'Not Interested',
                        'Interested',
                        'Switch Off',
                        'Invalid',
                        'Busy',
                        'Call Cut'
                      ].includes(noteForm.content) ? noteForm.content : ''}
                      style={{ 
                        marginBottom: '15px', 
                        width: '100%', 
                        padding: '10px 12px', 
                        borderRadius: '5px', 
                        border: '1px solid #ddd',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">-- Choose from standard options --</option>
                      <option value="Follow-up">Follow-up</option>
                      <option value="Not Pick Up">Not Pick Up</option>
                      <option value="Not Interested">Not Interested</option>
                      <option value="Interested">Interested</option>
                      <option value="Switch Off">Switch Off</option>
                      <option value="Invalid">Invalid</option>
                      <option value="Busy">Busy</option>
                      <option value="Call Cut">Call Cut</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Note / Customize Note *</label>
                    <textarea
                      value={noteForm.content}
                      onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                      placeholder="Enter your note..."
                      rows="4"
                      required
                    />
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="important"
                      checked={noteForm.isImportant}
                      onChange={(e) => setNoteForm({ ...noteForm, isImportant: e.target.checked })}
                    />
                    <label htmlFor="important">⭐ Mark as important</label>
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : '💾 Save Note'}
                  </button>
                </form>
              </div>

              <div className="notes-list">
                <h3>Notes</h3>
                {leadData.notes && leadData.notes.length > 0 ? (
                  <div className="notes-grid">
                    {leadData.notes
                      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                      .map((note, index) => (
                        <div key={index} className={`note-card ${note.isImportant ? 'important' : ''}`}>
                          {note.isImportant && <span className="important-badge">⭐ Important</span>}
                          <p>{note.content}</p>
                          <div className="note-footer">
                            <small>
                              {note.createdBy?.name} • {formatDate(note.createdAt)}
                            </small>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="no-data">No notes added yet</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadDetails;
